import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Label } from "../../../app/components/ui/label";
import { Input } from "../../../app/components/ui/input";
import { Button } from "../../../app/components/ui/button";
import { Switch } from "../../../app/components/ui/switch";
import { Separator } from "../../../app/components/ui/separator";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { useAuth } from "../../../providers/AuthProvider";

const ALL_TYPES = ["combo", "discount", "promocode", "bxgy", "cart"] as const;
type OfferType = (typeof ALL_TYPES)[number];

export function PromotionStackingPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);

  const [allowComboDiscount, setAllowComboDiscount] = useState(false);
  const [allowDiscountCoupon, setAllowDiscountCoupon] = useState(false);
  const [allowComboCoupon, setAllowComboCoupon] = useState(false);
  const [allowMultipleCoupons, setAllowMultipleCoupons] = useState(false);
  const [executionOrder, setExecutionOrder] = useState<OfferType[]>(["combo", "discount", "promocode", "bxgy", "cart"]);
  const [maxDiscountPercent, setMaxDiscountPercent] = useState<string>("");
  const [preventLoss, setPreventLoss] = useState(true);

  const orderLabel = useMemo(() => {
    const map: Record<string, string> = {
      combo: "Combo",
      discount: "Discount",
      promocode: "Coupon/Promo code",
      bxgy: "Buy X Get Y",
      cart: "Cart offer",
    };
    return map;
  }, []);

  async function load() {
    if (!token) return;
    setLoading(true);
    try {
      const res = await api.promotions.stacking.get({ token });
      const d = res.data;
      setAllowComboDiscount(Boolean(d.allow_combo_discount));
      setAllowDiscountCoupon(Boolean(d.allow_discount_coupon));
      setAllowComboCoupon(Boolean(d.allow_combo_coupon));
      setAllowMultipleCoupons(Boolean(d.allow_multiple_coupons));
      setExecutionOrder(
        Array.isArray(d.execution_order) && d.execution_order.length
          ? (d.execution_order.filter((x) => (ALL_TYPES as readonly string[]).includes(x)) as OfferType[])
          : ["combo", "discount", "promocode", "bxgy", "cart"],
      );
      setMaxDiscountPercent(d.max_discount_percent ?? "");
      setPreventLoss(Boolean(d.prevent_loss));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load stacking settings");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function move(type: OfferType, dir: -1 | 1) {
    setExecutionOrder((prev) => {
      const idx = prev.indexOf(type);
      if (idx < 0) return prev;
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const copy = [...prev];
      const tmp = copy[idx];
      copy[idx] = copy[nextIdx];
      copy[nextIdx] = tmp;
      return copy;
    });
  }

  async function onSave() {
    if (!token) return;
    const pct = maxDiscountPercent.trim();
    if (pct && (!Number.isFinite(Number(pct)) || Number(pct) < 0 || Number(pct) > 100)) {
      toast.error("Max discount % must be between 0 and 100");
      return;
    }

    setLoading(true);
    try {
      await api.promotions.stacking.update({
        token,
        payload: {
          allow_combo_discount: allowComboDiscount,
          allow_discount_coupon: allowDiscountCoupon,
          allow_combo_coupon: allowComboCoupon,
          allow_multiple_coupons: allowMultipleCoupons,
          execution_order: executionOrder,
          max_discount_percent: pct ? pct : null,
          prevent_loss: preventLoss,
        },
      });
      toast.success("Stacking settings saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save stacking settings");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-foreground mb-1">Offer stacking</h1>
        <p className="text-muted-foreground">Control which offers can combine, order of execution, and safety limits</p>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Combination rules</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-medium">Allow combo + discount</div>
              <div className="text-sm text-muted-foreground">If off, only one of these can apply</div>
            </div>
            <Switch checked={allowComboDiscount} onCheckedChange={setAllowComboDiscount} />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-medium">Allow discount + coupon</div>
              <div className="text-sm text-muted-foreground">Allow cart discounts to combine with promo codes</div>
            </div>
            <Switch checked={allowDiscountCoupon} onCheckedChange={setAllowDiscountCoupon} />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-medium">Allow combo + coupon</div>
              <div className="text-sm text-muted-foreground">Allow combo offers to combine with promo codes</div>
            </div>
            <Switch checked={allowComboCoupon} onCheckedChange={setAllowComboCoupon} />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-medium">Allow multiple coupons</div>
              <div className="text-sm text-muted-foreground">If off, only the first eligible coupon can apply</div>
            </div>
            <Switch checked={allowMultipleCoupons} onCheckedChange={setAllowMultipleCoupons} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Safety constraints</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
            <div className="space-y-1.5">
              <Label>Max discount % (optional)</Label>
              <Input
                value={maxDiscountPercent}
                onChange={(e) => setMaxDiscountPercent(e.target.value)}
                placeholder="e.g. 30"
                inputMode="decimal"
              />
              <div className="text-xs text-muted-foreground">Caps total discount against cart subtotal (pre-GST)</div>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border p-3">
              <div>
                <div className="font-medium">Prevent loss</div>
                <div className="text-sm text-muted-foreground">Disallow any line discounted below FIFO purchase price</div>
              </div>
              <Switch checked={preventLoss} onCheckedChange={setPreventLoss} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Execution order</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground">Use the arrows to reorder how offer types apply (top → bottom)</div>
          <Separator />
          <div className="space-y-2">
            {executionOrder.map((t, idx) => (
              <div key={t} className="flex items-center justify-between gap-3 rounded-md border p-3">
                <div className="font-medium">{orderLabel[t]}</div>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={idx === 0} onClick={() => move(t, -1)}>
                    Up
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={idx === executionOrder.length - 1}
                    onClick={() => move(t, 1)}
                  >
                    Down
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={() => void onSave()} disabled={loading}>
          {loading ? "Saving…" : "Save"}
        </Button>
        <Button variant="outline" onClick={() => void load()} disabled={loading}>
          Reload
        </Button>
      </div>
    </div>
  );
}

